import { useLocalStorage } from "@/hooks/useLocalStorage.ts";
import { cn } from "@/lib/utils.ts";
import {
  type PointerEvent as ReactPointerEvent,
  type RefObject,
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

export const DEFAULT_PANEL_MIN_WIDTH = 320;
// Aria fallback for `aria-valuemax` before the container has been measured; the
// real ceiling is the live `container − minOther` computed below.
export const DEFAULT_PANEL_MAX_WIDTH = 1000;
// Fallback used only for keyboard nudges before a width has been committed (the
// resting default is otherwise the CSS proportional width supplied by the host).
export const DEFAULT_PANEL_FALLBACK_WIDTH = 480;

/** Which side of the split the width-carrying panel sits on. */
export type PanelSide = "left" | "right";

type UseResizablePanelOptions = {
  /**
   * Side the width-carrying panel occupies. The drag handle lives on the panel's
   * inner edge — the right edge of a left panel, the left edge of a right panel —
   * so dragging *toward the content* widens it regardless of side.
   */
  side: PanelSide;
  /**
   * Which side's width the stored number is. Defaults to `side` — the panel that
   * carries the width, which is what a drag sets directly.
   *
   * Pass the opposite side where the flex column is the one the user is choosing
   * a width for. Dragging the thread list's divider means "make the thread list
   * this wide"; storing the space beside it records a number that only means
   * that at the width it was measured at, so every later resize comes out of the
   * list alone until it hits its floor.
   */
  persistedSide?: PanelSide;
  /**
   * localStorage key for the committed width (client-side, per device). Share one
   * key across surfaces to keep a single divider width (e.g. the canvas split on
   * home and thread detail).
   */
  storageKey: string;
  /** Min width the width-carrying panel keeps. */
  min?: number;
  /**
   * Min width the opposite (flex) panel keeps. The width-carrying panel can't grow
   * past `container − minOther`, so both columns stay usable at any divider
   * position — a symmetric floor rather than an arbitrary max.
   */
  minOther?: number;
  /**
   * Max width the opposite (flex) panel may reach — the mirror of `minOther`.
   * The flex column has no width of its own to bound, so its ceiling is imposed
   * by flooring this panel at `container − maxOther`. That floor rises with the
   * container, which is the point: without it the flex column absorbs every
   * extra pixel of viewport and has no ceiling on a wide display.
   */
  maxOther?: number;
  /**
   * Max width this panel may be dragged to. Left off, the only ceiling is
   * `container − minOther`, which on a wide display is most of the window — fine
   * for a column the user is reading in, wrong for one that holds a single card
   * and starts reading as a modal past a certain width.
   *
   * With `keepOther` this is a reach rather than a cap: see there.
   */
  max?: number;
  /**
   * Width the flex column keeps against a drag past `max`. With it, `max` is
   * where this panel may always be dragged to (inside `container − minOther`),
   * and past it the panel keeps growing as long as the flex column stays this
   * wide — so a wide display hands the panel its spare width without a narrow
   * one losing any of the reach it had under the plain cap.
   */
  keepOther?: number;
  /** Seed for the first keyboard nudge before any width is committed. */
  defaultWidth?: number;
  /** The split container, measured live so the drag ceiling tracks its width. */
  containerRef: RefObject<HTMLElement | null>;
};

/** Hold `value` inside `[floor, ceiling]`; an absent ceiling is unbounded. */
const clampWidth = (value: number, floor: number, ceiling: number | null | undefined) =>
  Math.min(ceiling ?? Number.POSITIVE_INFINITY, Math.max(floor, value));

/**
 * How wide the flex column has settled at, or `null` while nothing is
 * committed — for a caller deciding what to render *in* that column.
 *
 * Which answer applies depends on which column the stored number describes,
 * and the two differ in more than arithmetic: one is available immediately and
 * the other has to wait for a measurement.
 */
const settledFlexWidth = ({
  storedWidth,
  storesFlexColumn,
  minOther,
  maxOther,
  containerWidth,
  clampSelf,
}: {
  storedWidth: number | null;
  storesFlexColumn: boolean;
  minOther: number;
  maxOther: number | undefined;
  containerWidth: number | null;
  /** Clamps a width into *this* panel's range, which is not the flex column's. */
  clampSelf: (value: number) => number;
}): number | null => {
  if (storedWidth == null) return null;
  // The stored number is that column's own, so it needs no measurement and is
  // right on the first render. Only its own range applies.
  if (storesFlexColumn) return clampWidth(storedWidth, minOther, maxOther);
  // Otherwise it can only be whatever the container has left once this panel
  // takes its share, and that waits for a measurement.
  if (containerWidth == null) return null;
  return containerWidth - clampSelf(storedWidth);
};

/**
 * Drag-to-resize state for the width-carrying panel of a {@link SplitPanelLayout}.
 * The committed width is persisted in `localStorage` (so each device keeps its own
 * preferred width); while dragging we track a transient `dragWidth` and only write
 * the final value back on pointer-up — avoiding a storage write per pointer-move.
 *
 * The drag range is `[min, container − minOther]`, so neither column can be
 * crushed below its floor. Passing `maxOther` additionally raises this panel's
 * floor to `container − maxOther`, capping how wide the flex column can get, and
 * `max` lowers its ceiling to a fixed width of its own — or, with `keepOther`,
 * guarantees it that width and lets it grow past it while the flex column keeps
 * `keepOther`. The container is measured live, so every bound derived from it
 * follows viewport/layout changes.
 *
 * The returned `width` is consumed via a CSS variable (`--panel-w`) so the host's
 * container-query breakpoints still decide *whether* the fixed width applies
 * (desktop) or the panel stays full-bleed (mobile). `width === null` means no
 * width is committed yet → the host falls back to its CSS proportional default.
 */
export const useResizablePanel = ({
  side,
  persistedSide = side,
  storageKey,
  min = DEFAULT_PANEL_MIN_WIDTH,
  minOther = DEFAULT_PANEL_MIN_WIDTH,
  maxOther,
  max: maxSelf,
  keepOther,
  defaultWidth = DEFAULT_PANEL_FALLBACK_WIDTH,
  containerRef,
}: UseResizablePanelOptions) => {
  // Live container width so the ceiling = container − minOther. Measured before
  // paint so an immediate drag/nudge already has the real bound.
  const [containerWidth, setContainerWidth] = useState<number | null>(null);
  // Mirrored in a ref for the write path. A drag installs its pointer listeners
  // once and they run until it ends, so converting against state would convert
  // against the box the drag *started* in — a window resized mid-drag would then
  // persist a number measured against a container that no longer exists.
  const containerWidthRef = useRef<number | null>(null);
  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    // A zero width means the split is not laid out yet — a test environment with
    // no layout, or a panel still off-screen. Treat it as unmeasured rather than
    // as a real container, so the bounds derived below stay unbounded until
    // there is a width to derive them from. Measured as zero they would collide,
    // and a collision is how the hook decides a drag has nowhere to go.
    const update = () => {
      const { width } = el.getBoundingClientRect();
      const measured = width > 0 ? width : null;
      containerWidthRef.current = measured;
      setContainerWidth(measured);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [containerRef]);

  // Capping the flex column means flooring this one: it can't give away more
  // than `container − maxOther` without the other side exceeding its ceiling.
  const floor =
    containerWidth != null && maxOther != null ? Math.max(min, containerWidth - maxOther) : min;
  // Two ceilings, whichever binds first: the space left after the other column
  // keeps its floor, and this panel's own cap. The cap needs no measurement, so
  // it also bounds the drag before the container has been measured.
  const spaceCeiling = containerWidth != null ? containerWidth - minOther : null;
  let ceiling: number | null;
  if (keepOther != null) {
    // The cap gives way: past it the panel may take whatever the flex column
    // has beyond `keepOther`, so the ceiling is the greater of the two, still
    // inside the space the hard floor leaves. Open until measured — unlike a
    // cap, a reach means nothing without the container, and clamping to it
    // meanwhile would lay out a committed width past the reach *at* the reach
    // and then slide it out, since the grid transitions its tracks.
    ceiling =
      containerWidth != null
        ? Math.min(containerWidth - minOther, Math.max(maxSelf ?? 0, containerWidth - keepOther))
        : null;
  } else {
    ceiling =
      spaceCeiling != null && maxSelf != null
        ? Math.min(spaceCeiling, maxSelf)
        : (spaceCeiling ?? maxSelf ?? null);
  }
  // The floor wins a collision: a column crushed below its minimum is unusable,
  // while one a few pixels over its cap is merely wider than intended.
  const max = ceiling != null ? Math.max(floor, ceiling) : null;
  const clamp = useCallback((value: number) => clampWidth(value, floor, max), [floor, max]);

  // `null` = no committed width → the panel uses its CSS proportional default. A
  // drag/keyboard resize commits a px override that then wins.
  const [rawStoredWidth, setRawStoredWidth, clearStoredWidth] = useLocalStorage<number | null>(
    storageKey,
    null,
  );

  const storesFlexColumn = persistedSide !== side;

  // The stored number in this panel's own terms, for the drag paths that think
  // in it. Reads the container from the ref rather than from state so a
  // conversion is never made against a stale box.
  const toSelfWidth = useCallback(
    (stored: number | null): number | null => {
      if (stored == null) return null;
      if (!storesFlexColumn) return stored;
      const container = containerWidthRef.current;
      return container != null ? container - stored : null;
    },
    [storesFlexColumn],
  );

  const setStoredWidth = useCallback(
    (next: number | ((prev: number | null) => number)) => {
      setRawStoredWidth((prevStored) => {
        const container = containerWidthRef.current;
        const self = typeof next === "function" ? next(toSelfWidth(prevStored)) : next;
        if (!storesFlexColumn) return self;
        // Unmeasured: a number written under a key that means the other column
        // would say the opposite of what it means, so leave what is there.
        return container != null ? container - self : prevStored;
      });
    },
    [setRawStoredWidth, storesFlexColumn, toSelfWidth],
  );
  const [dragWidth, setDragWidth] = useState<number | null>(null);
  // Latest in-drag width, mirrored in a ref so pointer-up can commit it from the
  // handler body rather than via a (side-effect-bearing) state updater.
  const dragWidthRef = useRef<number | null>(null);

  // A left panel widens as the pointer moves right (+dx); a right panel widens as
  // the pointer moves left (−dx). One sign flip captures the whole difference.
  const dir = side === "left" ? 1 : -1;

  const startResize = useCallback(
    (e: ReactPointerEvent<HTMLElement>) => {
      // Primary button / touch / pen only.
      if (e.button !== 0) return;
      e.preventDefault();
      const handle = e.currentTarget;
      handle.setPointerCapture(e.pointerId);
      const startX = e.clientX;
      // First drag from the proportional default (no stored width) seeds from the
      // panel's current rendered width so the handle doesn't jump.
      const base = clamp(
        toSelfWidth(rawStoredWidth) ?? handle.parentElement?.offsetWidth ?? defaultWidth,
      );
      dragWidthRef.current = base;
      setDragWidth(base);

      const onMove = (ev: PointerEvent) => {
        const next = clamp(base + dir * (ev.clientX - startX));
        dragWidthRef.current = next;
        setDragWidth(next);
      };
      const cleanup = (ev: PointerEvent) => {
        handle.releasePointerCapture?.(ev.pointerId);
        handle.removeEventListener("pointermove", onMove);
        handle.removeEventListener("pointerup", onCommit);
        handle.removeEventListener("pointercancel", onCancel);
      };
      // pointerup → persist the final width.
      const onCommit = (ev: PointerEvent) => {
        cleanup(ev);
        if (dragWidthRef.current !== null) setStoredWidth(dragWidthRef.current);
        dragWidthRef.current = null;
        setDragWidth(null);
      };
      // pointercancel (scroll intent, second touch, system gesture) → discard the
      // in-progress drag and revert to the stored width; don't persist an
      // intermediate position the user never settled on.
      const onCancel = (ev: PointerEvent) => {
        cleanup(ev);
        dragWidthRef.current = null;
        setDragWidth(null);
      };
      handle.addEventListener("pointermove", onMove);
      handle.addEventListener("pointerup", onCommit);
      handle.addEventListener("pointercancel", onCancel);
    },
    [clamp, rawStoredWidth, toSelfWidth, setStoredWidth, defaultWidth, dir],
  );

  // Keyboard resize: nudge the committed width by a step. A functional updater
  // (which `useLocalStorage` resolves against the freshly-read stored value)
  // makes held-key repeats chain — each press advances from the latest committed
  // width regardless of render timing. `seedPx` (the panel's measured width) is
  // only consulted for the first nudge off the proportional default.
  const nudgeWidth = useCallback(
    (delta: number, seedPx: number) => {
      setStoredWidth((prev) => clamp((prev ?? seedPx) + delta));
    },
    [clamp, setStoredWidth],
  );

  // Drop the committed width so the panel falls back to the host's proportional
  // default — the width it opens at before anyone drags. Removing the key rather
  // than storing `null` keeps "never chose a width" and "chose the default" the
  // same state, which is what the user means by resetting.
  const resetWidth = useCallback(() => clearStoredWidth(), [clearStoredWidth]);

  // The flex column's bounds are this panel's, mirrored: what caps one floors
  // the other. Written as CSS so the committed width can be expressed without a
  // measurement; `min`/`max` collapse to the literal wherever a bound is open.
  let flexLower: string;
  if (keepOther != null) {
    flexLower = `max(${minOther}px, min(calc(100cqw - ${maxSelf ?? 0}px), ${keepOther}px))`;
  } else if (maxSelf != null) {
    flexLower = `max(${minOther}px, calc(100cqw - ${maxSelf}px))`;
  } else {
    flexLower = `${minOther}px`;
  }
  const flexUpper =
    maxOther != null ? `min(${maxOther}px, calc(100cqw - ${min}px))` : `calc(100cqw - ${min}px)`;

  // What to render, as CSS rather than a number.
  //
  // Where the stored value is the flex column's, the subtraction is CSS's to do.
  // In JS it would make the rendered width depend on a measurement, and a
  // measurement is not available on the first render — which matters here
  // because the grid transitions `grid-template-columns`. A first render that
  // fell back to the resting width would not be an invisible pre-paint state; it
  // would arm a 200ms slide from the resting width to the committed one on every
  // load. `100cqw` is the right box for the one split that persists its flex
  // column: it fills its container query's own, which is the same equivalence
  // the resting default below already relies on.
  let widthCss: string | null = null;
  if (dragWidth != null) widthCss = `${clamp(dragWidth)}px`;
  else if (rawStoredWidth != null) {
    widthCss = storesFlexColumn
      ? `calc(100cqw - clamp(${flexLower}, ${rawStoredWidth}px, ${flexUpper}))`
      : `${clamp(rawStoredWidth)}px`;
  }

  return {
    /** The committed width as a CSS length, or `null` while none is committed. */
    widthCss,
    committedFlexWidth: settledFlexWidth({
      storedWidth: rawStoredWidth,
      storesFlexColumn,
      minOther,
      maxOther,
      containerWidth,
      clampSelf: clamp,
    }),
    isResizing: dragWidth !== null,
    startResize,
    nudgeWidth,
    resetWidth,
    // The live floor, not the nominal `min` — the separator reports it as
    // `aria-valuemin`, so it has to be the bound a drag actually hits.
    min: floor,
    max: max ?? DEFAULT_PANEL_MAX_WIDTH,
  };
};

const KEYBOARD_STEP = 16;

/**
 * Thin draggable divider on the resizable panel's inner edge. Hidden below `@3xl`,
 * where the split collapses to one panel at a time and resizing doesn't apply. The
 * parent panel must be `relative` for the absolute positioning to anchor.
 *
 * Drag to resize, arrow keys to nudge, double-click to return to the resting
 * default.
 */
export const PanelResizeHandle = ({
  side,
  onResizeStart,
  onNudgeWidth,
  onResetWidth,
  isResizing,
  min = DEFAULT_PANEL_MIN_WIDTH,
  max = DEFAULT_PANEL_MAX_WIDTH,
}: {
  side: PanelSide;
  onResizeStart: (e: ReactPointerEvent<HTMLElement>) => void;
  /** Nudge the width by a step; the measured width seeds the first nudge. */
  onNudgeWidth: (delta: number, seedPx: number) => void;
  /** Drop the committed width and return the panel to its resting default. */
  onResetWidth: () => void;
  isResizing: boolean;
  min?: number;
  max?: number;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  // The panel's actual rendered width — tracked so `aria-valuenow` is always
  // present (WAI-ARIA requires it for a focusable separator) and the first
  // keyboard nudge seeds from the real size even on the proportional default.
  // `useLayoutEffect` measures before paint, so an immediate focus never reads
  // the fallback placeholder.
  const [measuredWidth, setMeasuredWidth] = useState(DEFAULT_PANEL_FALLBACK_WIDTH);
  useLayoutEffect(() => {
    const panel = ref.current?.parentElement;
    if (!panel) return;
    const update = () => setMeasuredWidth(Math.round(panel.getBoundingClientRect().width));
    update();
    const ro = new ResizeObserver(update);
    ro.observe(panel);
    return () => ro.disconnect();
  }, []);

  // Arrow keys grow the panel toward its content: a left panel grows on
  // ArrowRight, a right panel grows on ArrowLeft.
  const growKey = side === "left" ? "ArrowRight" : "ArrowLeft";

  return (
    // Window-splitter pattern: a focusable separator the user drags with the
    // pointer or nudges with the arrow keys.
    // biome-ignore lint/a11y/useSemanticElements: <hr> can't host the drag handle + visual rule
    <div
      ref={ref}
      role="separator"
      aria-orientation="vertical"
      aria-label="Resize panel"
      aria-valuenow={measuredWidth}
      aria-valuemin={min}
      aria-valuemax={max}
      tabIndex={0}
      onPointerDown={onResizeStart}
      // Double-click snaps back to the resting default. The two clicks each run a
      // zero-distance drag first, committing the width the divider already has;
      // `dblclick` lands after both `pointerup`s, so the reset is what survives.
      onDoubleClick={onResetWidth}
      onKeyDown={(e) => {
        if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
          e.preventDefault();
          onNudgeWidth(e.key === growKey ? KEYBOARD_STEP : -KEYBOARD_STEP, measuredWidth);
        }
      }}
      className={cn(
        // z-60 clears the canvas card's scrim (z-50), which spans the whole
        // canvas column and would otherwise swallow the drag handle. Resizing
        // is the one affordance deliberately exempted from the scrim: the card
        // dims and blocks the column's own controls (month title, view
        // switcher, account switcher) on purpose, but the split between the
        // panels belongs to the window rather than to the card, and a user who
        // opens a card in a cramped column still needs to widen it. Drop this
        // back to z-20 only if resizing under a card stops being wanted.
        "absolute inset-y-0 z-60 hidden w-4 cursor-col-resize touch-none select-none @3xl:flex items-stretch group focus-visible:outline-none",
        // Wide (16px) hit area for easy grabbing — it extends inward into the
        // panel's divider gutter — while the 1px rule stays pinned to the panel
        // edge so it lines up flush with adjacent content borders (e.g. the
        // canvas header's bottom rule).
        side === "left" ? "right-0 justify-end" : "left-0 justify-start",
      )}
    >
      {/* 1px rule: a faint resting seam (so the divider is discoverable) that
          brightens on hover / focus / while dragging. */}
      <div
        className={cn(
          "w-px transition-colors",
          isResizing
            ? "bg-muted-foreground"
            : "bg-border group-hover:bg-muted-foreground/50 group-focus-visible:bg-muted-foreground/50",
        )}
      />
    </div>
  );
};
